import {
  OnlinePricingTemplate,
  PartnerBucketGroupPrice,
  PartnerBucketGroups,
} from "./partnerDeal.interface";
import {
  OnlineBucketGroupCoverageEntry,
  OnlineGroupKey,
  OnlinePricingStructure,
} from "./onlinePricing.interface";
import { PlatformType } from "./paxTerminal.interface";
import { KlearlyCountryAlpha2Code } from "./klearlyCountryAlpha2Code.enum";

// The groups the four-bucket shape prices. Named separately from the storage
// type below so the bucket-key lists that drive the four-bucket forms still
// have something exhaustive to check themselves against.
export type OnlineFourBucketPartnerGroupKey =
  | "iDeal"
  | "bancontact"
  | "amex"
  | "corporateCreditCards"
  | "nonCorporateCreditCards"
  | "appleGooglePay";

// A partner's online rates, in whichever shape the partner is priced on. Both
// live on the same fields — `defaultOnlinePricing`, `softwareVendorOnlinePricing`
// and `onlinePurchPriceProfiles[].bucketGroups` — and the backend reads the keys
// present to tell them apart (detectOnlinePricingStructure), so nothing here can
// require one shape's keys. Permissive for the same reason BusinessOnlinePricing
// is: a partner priced on sixteen buckets is not a type error.
export type OnlinePartnerBucketGroups = Partial<
  Record<OnlineGroupKey, PartnerBucketGroupPrice>
>;

// The range shape to start a partner's online pricing from while nothing is
// frozen in Adyen. Field names match IOnlinePurchPriceProfile so it can be
// posted back as a range.
export type IDefaultOnlinePriceRange = {
  onlinePricingStructure: OnlinePricingStructure;
  onlineCardSplitTemplate: PricingTemplate;
  pricingTemplate: OnlinePricingTemplate;
  bucketGroups: Partial<OnlinePartnerBucketGroups>;
};

export type IPartner = IPartnerDetails & {
  _id: string;
  purchPriceProfiles: IPurchPriceProfile[];
  onlinePurchPriceProfiles?: IOnlinePurchPriceProfile[];
  createdAt: string;
  teamMembers: IPartnerTeamMember[];
  refCode: string;
  defaultPricing: PartnerBucketGroups;
  softwareVendorPricing: PartnerBucketGroups;
  pricingTemplate?: PricingTemplate;
  softwareVendorPricingTemplate?: PricingTemplate;
  terminalCouplingPlatform: PlatformType;
  partnerType: PartnerType;
  role: PartnerRole;
  settings?: IPartnerSettings;
  softwareVendorId?: string;
  defaultOnlinePricing?: OnlinePartnerBucketGroups;
  defaultOnlinePricingTemplate?: OnlinePricingTemplate;
  softwareVendorOnlinePricing?: OnlinePartnerBucketGroups;
  softwareVendorOnlinePricingTemplate?: OnlinePricingTemplate;
  // Computed by the partner-details read (GET /partners/:partnerId) and by no
  // other partner response: whether anything downstream freezes the online
  // pricing structure, and the shape to offer while nothing does.
  hasMerchantsSubmittedToAdyen?: boolean;
  defaultOnlinePriceRange?: IDefaultOnlinePriceRange;
};

export type IPartnerDetails = {
  name: string;
  ownerName: string;
  phone: { number: string; countryCode: string };
  email: string;
  country?: KlearlyCountryAlpha2Code;
};

export type InPersonBucketGroupCoverageEntry = {
  group: string | null;
  feeNames: string[];
};

export type IPurchPriceProfile = {
  _id: string;
  name: string;
  bucketGroups: IBucketGroups;
  // Per-range mirrors of the partner-level sections. Absent falls back to those
  // at read time, so no stored range needs a backfill.
  defaultPricing?: Partial<PartnerBucketGroups>;
  softwareVendorPricing?: Partial<PartnerBucketGroups>;
  whitelist?: string[];
  pricingTemplate?: PricingTemplate;
  inPersonBucketGroupCoverage?: InPersonBucketGroupCoverageEntry[];
};

export type IOnlinePurchPriceProfile = {
  _id: string;
  name: string;
  bucketGroups: Partial<OnlinePartnerBucketGroups>;
  defaultOnlinePricing?: Partial<OnlinePartnerBucketGroups>;
  softwareVendorOnlinePricing?: Partial<OnlinePartnerBucketGroups>;
  whitelist?: string[];
  // The two template axes a range is priced on, independent of one another:
  // `pricingTemplate` is the local payment method, `onlineCardSplitTemplate` the
  // card split. The split has no default on the wire — a range that declares
  // none prices PricingTemplate.Default, which is why no stored range needs a
  // backfill to stay valid.
  pricingTemplate?: OnlinePricingTemplate;
  onlineCardSplitTemplate?: PricingTemplate;
  onlinePricingStructure?: OnlinePricingStructure;
  onlineBucketGroupCoverage?: OnlineBucketGroupCoverageEntry[];
  // Computed by the partner-details read alone, and carried by every stored
  // range once it is: whether a merchant submitted to Adyen is priced from this
  // one. Absent only until that read ships the field.
  hasMerchantsSubmittedToAdyen?: boolean;
};
export enum PricingTemplate {
  Default = "default", //domestic debit, european, international and commercial
  EuropeanDebitSplit = "europeanDebitSplit", //domestic debit, european debit, european, international and commercial
  DomesticCreditSplit = "domesticCreditSplit", //domestic debit, domestic credit, european, international and commercial
}
export type PartnerBucketGroupMinMaxPrice = PartnerBucketGroupPrice & {
  max: number;
  min?: number;
};
export interface IBucketGroups {
  domesticDebitCards: PartnerBucketGroupMinMaxPrice;
  domesticCreditCards?: PartnerBucketGroupMinMaxPrice;
  europeanDebitCards?: PartnerBucketGroupMinMaxPrice;
  european: PartnerBucketGroupMinMaxPrice;
  internationalAndCommercial: PartnerBucketGroupMinMaxPrice;
  amex?: PartnerBucketGroupMinMaxPrice;
}
export type BucketGroupFieldNames =
  | "domesticDebitCards"
  | "domesticCreditCards"
  | "europeanDebitCards"
  | "european"
  | "internationalAndCommercial"
  | "amex";

export type IPartnerTeamMember = {
  name: string;
  email: string;
  _id: string;
  createdAt: string;
};
export interface IPartnerSettings {
  webhooks: {
    merchantVerified: { url: string };
    paymentComplete: { url: string };
  };
}
export enum PartnerType {
  SoftwareVendor = "SoftwareVendor", // Independent Software Vendor (ISV)
  Reseller = "Reseller", // Value-Added Reseller (VAR)
  FullServicePartner = "FullServicePartner", // Full-Service Partner (software vendor + reseller)
  FullServiceWithResellers = "FullServiceWithResellers", // Full-Service Partner with resellers (internal + external resellers)
}
export enum PartnerRole {
  Admin = "Admin", // Admin
  User = "User", // User
}
